import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BlogPost, Idea, Maybe, NotificationType, Scalars, User } from '../../../common/core-api';
import { useFile, useIsNarrowMobile } from '../../../hooks';
import { removeTags } from '../../../helpers';
import { DefaultIdeaImage, FunnelChallengeIcon, MilestoneChallengeIcon } from '../../../common/icons';
import { NotificationCardTypes, UserType } from '../../../common/enums';
import { RelativeDateWithTooltip, ResponsiveImage, Tooltip } from '../../../common/components';
import { NOTIF_STREAM_DEFAULT_IMAGE_HEIGHT } from '../../shared-constants';
import * as Styled from './IdeaContent.styled';
import { Footer, UserLink } from '.';

export const IdeaContent = ({
  item,
  author,
  showImage,
  type,
  cardType = NotificationCardTypes.IDEA,
  hideAuthorName,
  time,
  isModeratorNotification,
  phaseId,
  isValid
}: {
  item?: Maybe<BlogPost | Idea>;
  author?: Maybe<User>;
  showImage: boolean;
  cardType?: NotificationCardTypes;
  type: NotificationType;
  hideAuthorName?: boolean;
  time?: string;
  isModeratorNotification?: boolean;
  phaseId?: Maybe<Scalars['String']>;
  isValid: boolean;
}) => {
  const intl = useIntl();
  const [getFile] = useFile();
  const isNarrowMobile = useIsNarrowMobile();

  const { id, text, title, files } = item || {};

  const message = intl.formatMessage({
    id: 'youNoLongerHaveAccessToThisItem',
    defaultMessage: 'You no longer have access to this item'
  });
  const ideaImage = files?.images && files?.images[0];
  const defaultIdeaImageSize = 120;
  const imageSrc =
    (ideaImage && getFile({ file: ideaImage, width: defaultIdeaImageSize, height: defaultIdeaImageSize })) || '';

  const imageResizeCallback = (width: number) => {
    if (!ideaImage) {
      return '';
    }

    let src: string = imageSrc;
    if (isNarrowMobile) {
      src = getFile({ file: ideaImage, width, height: NOTIF_STREAM_DEFAULT_IMAGE_HEIGHT });
    }

    if (!isNarrowMobile && width !== defaultIdeaImageSize) {
      src = getFile({ file: ideaImage, width: defaultIdeaImageSize, height: defaultIdeaImageSize });
    }

    return src;
  };

  const linkUrl = cardType === NotificationCardTypes.BLOG_POST ? `/blogpost/${id}` : `/post/${id}`;
  const { displayName, userAccess } = author || {};
  const isAuthorAdmin = [UserType.SUPER_ADMIN, UserType.ADMIN].includes(Number(userAccess?.userType));
  const clearTitle = removeTags(title || '');

  if (!item || !author) {
    return null;
  }

  return (
    <Styled.ContentContainer data-testid="IdeaContent">
      {showImage &&
        (imageSrc ? (
          <Styled.IdeaImageWrapper>
            <ResponsiveImage callback={imageResizeCallback} altText={title || ''} />
          </Styled.IdeaImageWrapper>
        ) : (
          !isNarrowMobile && (
            <Styled.DefaultImageContainer>
              <DefaultIdeaImage />
            </Styled.DefaultImageContainer>
          )
        ))}
      <Styled.Content>
        <Styled.TitleContainer>
          {type === NotificationType.IDEA_PHASE_REACHED && (
            <Styled.IconContainer>
              <FunnelChallengeIcon />
            </Styled.IconContainer>
          )}
          {type === NotificationType.IDEA_MILESTONE_REACHED && (
            <Styled.IconContainer>
              <MilestoneChallengeIcon />
            </Styled.IconContainer>
          )}
          {isValid ? (
            <Styled.IdeaTitle href={linkUrl} target="_blank" rel="noreferrer">
              {clearTitle}
            </Styled.IdeaTitle>
          ) : (
            <Tooltip content={message}>
              <Styled.IdeaTitleGreyout target="_blank" rel="noreferrer">
                {clearTitle}
              </Styled.IdeaTitleGreyout>
            </Tooltip>
          )}
          {time && <RelativeDateWithTooltip time={time} />}
        </Styled.TitleContainer>
        {!hideAuthorName && (
          <Styled.ByAuthor>
            <FormattedMessage
              id="byAuthor"
              defaultMessage="by <a>{authorName} {admin, plural, one {(Admin)} other {}}</a>"
              values={{
                authorName: displayName,
                admin: isAuthorAdmin ? 1 : 2,
                a: (msg: string) =>
                  UserLink(msg, author.id, intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' }), false)
              }}
            />
          </Styled.ByAuthor>
        )}
        <Styled.IdeaDescription lines={isModeratorNotification ? 3 : 2} maxWidth="100%">
          {removeTags(text || '')}
        </Styled.IdeaDescription>
        {!isModeratorNotification && isValid && <Footer item={item} type={cardType} />}
      </Styled.Content>
    </Styled.ContentContainer>
  );
};
